import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { createStore } from './baseStore.js';
import { quoteService } from '../services/quoteService.js';

/**
 * @typedef {Object} QuoteItem
 * @property {number} id - Item ID
 * @property {number} quote_id - Parent quote ID
 * @property {string} description - Item description
 * @property {number} quantity - Item quantity
 * @property {number} unit_price - Unit price
 * @property {number} total - Total price (quantity * unit_price)
 */

/**
 * @typedef {Object} QuoteDocument
 * @property {number} id - Document ID
 * @property {number} quote_id - Parent quote ID
 * @property {string} type - Document type
 * @property {string} file_path - File path
 * @property {string} [description] - Document description
 */

/**
 * @typedef {Object} Quote
 * @property {number} id - Quote ID
 * @property {string} quote_number - Quote number
 * @property {number|null} site_id - Site ID
 * @property {number|null} tender_id - Tender ID (for versioning)
 * @property {string|null} option_name - Option name (for versioning)
 * @property {number} version - Quote version number
 * @property {('draft'|'pending'|'sent'|'accepted'|'rejected'|'expired'|'archived')} status - Quote status
 * @property {number} total - Quote total
 * @property {number} subtotal - Quote subtotal
 * @property {number} tax - Tax amount
 * @property {number} default_markup - Default markup percentage
 * @property {string|null} description - Quote description
 * @property {string|null} created_at - Creation timestamp
 * @property {string|null} updated_at - Update timestamp
 * @property {string|null} valid_until - Expiration date
 * @property {Object} metadata - Additional metadata
 * @property {QuoteItem[]} items - Quote items
 * @property {QuoteDocument[]} documents - Quote documents
 * @property {Array} labor_items - Labor items
 * @property {boolean} isExpired - Whether quote is expired
 * @property {string} statusLabel - Human-readable status
 * @property {string} statusVariant - Status color variant
 */

/**
 * @typedef {Object} QuoteFilters
 * @property {string|null} status - Filter by status
 * @property {number|null} organization_id - Filter by organization
 * @property {number|null} site_id - Filter by site
 * @property {string|null} date_from - Filter by start date
 * @property {string|null} date_to - Filter by end date
 * @property {string} search - Search term
 */

/**
 * @typedef {Object} QuoteStatistics
 * @property {number} total - Total quotes
 * @property {number} draft - Draft quotes
 * @property {number} pending - Pending quotes
 * @property {number} sent - Sent quotes
 * @property {number} accepted - Accepted quotes
 * @property {number} rejected - Rejected quotes
 * @property {number} expired - Expired quotes
 * @property {number} totalValue - Total value of all quotes
 * @property {number} acceptedValue - Total value of accepted quotes
 * @property {number} averageValue - Average quote value
 */

// Create the base quotes store using our store patterns
const useBaseQuoteStore = createStore('quotes', {
  apiEndpoint: '/api/quotes',
  idField: 'id',
  cacheTimeout: 10 * 60 * 1000, // 10 minutes for quotes
  enableOptimistic: true,
  enableCache: true,
  enableSync: true,
  
  transformData: (quote) => ({
    ...quote,
    // Ensure proper data types
    id: parseInt(quote.id),
    organization_id: quote.organization_id ? parseInt(quote.organization_id) : null,
    site_id: quote.site_id ? parseInt(quote.site_id) : null,
    total: parseFloat(quote.total || 0),
    subtotal: parseFloat(quote.subtotal || 0),
    tax: parseFloat(quote.tax || 0),
    markup: parseFloat(quote.markup || 0),
    
    // Ensure dates are properly formatted
    created_at: quote.created_at ? new Date(quote.created_at).toISOString() : null,
    updated_at: quote.updated_at ? new Date(quote.updated_at).toISOString() : null,
    expires_at: quote.expires_at ? new Date(quote.expires_at).toISOString() : null,
    
    // Parse JSON fields
    metadata: typeof quote.metadata === 'string' ? JSON.parse(quote.metadata || '{}') : (quote.metadata || {}),
    
    // Ensure arrays
    items: Array.isArray(quote.items) ? quote.items : [],
    documents: Array.isArray(quote.documents) ? quote.documents : [],
    labor_items: Array.isArray(quote.labor_items) ? quote.labor_items : [],
    
    // Computed fields
    get isExpired() {
      return quote.expires_at && new Date(quote.expires_at) < new Date();
    },
    
    get statusLabel() {
      const labels = {
        draft: 'Draft',
        pending: 'Pending Review',
        accepted: 'Accepted',
        rejected: 'Rejected',
        expired: 'Expired',
      };
      return labels[quote.status] || quote.status;
    },
    
    get statusVariant() {
      const variants = {
        draft: 'secondary',
        pending: 'warning',
        accepted: 'success',
        rejected: 'danger',
        expired: 'secondary',
      };
      return variants[quote.status] || 'secondary';
    },
  }),
  
  validateData: (quote) => {
    return quote &&
           typeof quote.id !== 'undefined' &&
           typeof quote.status === 'string' &&
           quote.status.length > 0;
  },
});

/**
 * Enhanced quotes store with all functionality including versioning and document management
 * @returns {Object} Quote store with state, computed properties, and actions
 */
export const useQuoteStore = defineStore('quotes', () => {
  const baseStore = useBaseQuoteStore();

  // Cache for in-flight quote fetches to prevent duplicates
  const fetchPromiseCache = new Map();

  // Additional state for quotes
  const selectedQuoteId = ref(null);
  const quoteFilters = ref({
    status: null,
    organization_id: null,
    site_id: null,
    date_from: null,
    date_to: null,
    search: '',
  });
  const quoteSortBy = ref('created_at');
  const quoteSortOrder = ref('desc');

  // Enhanced computed properties
  const selectedQuote = computed(() => {
    return selectedQuoteId.value ? baseStore.getById(selectedQuoteId.value) : null;
  });

  const filteredQuotes = computed(() => {
    try {
      const itemsList = baseStore.itemsList;
      let quotes = Array.isArray(itemsList) ? itemsList : [];

      // Apply status filter
      if (quoteFilters.value.status) {
        quotes = quotes.filter((quote) => quote.status === quoteFilters.value.status);
      }

      // Apply organization filter
      if (quoteFilters.value.organization_id) {
        quotes = quotes.filter((quote) => quote.organization_id === quoteFilters.value.organization_id);
      }

      // Apply site filter
      if (quoteFilters.value.site_id) {
        quotes = quotes.filter((quote) => quote.site_id === quoteFilters.value.site_id);
      }

      // Apply date range filter
      if (quoteFilters.value.date_from) {
        const fromDate = new Date(quoteFilters.value.date_from);
        quotes = quotes.filter((quote) => new Date(quote.created_at) >= fromDate);
      }

      if (quoteFilters.value.date_to) {
        const toDate = new Date(quoteFilters.value.date_to);
        quotes = quotes.filter((quote) => new Date(quote.created_at) <= toDate);
      }

      // Apply search filter
      if (quoteFilters.value.search) {
        const searchTerm = quoteFilters.value.search.toLowerCase();
        quotes = quotes.filter((quote) => {
          return (
            quote.quote_number?.toLowerCase().includes(searchTerm) ||
          quote.description?.toLowerCase().includes(searchTerm) ||
          quote.customer_name?.toLowerCase().includes(searchTerm) ||
          quote.site_name?.toLowerCase().includes(searchTerm)
          );
        });
      }

      // Apply sorting
      quotes.sort((a, b) => {
        const aVal = a[quoteSortBy.value];
        const bVal = b[quoteSortBy.value];
      
        let comparison = 0;
        if (aVal < bVal) {comparison = -1;}
        if (aVal > bVal) {comparison = 1;}
      
        return quoteSortOrder.value === 'desc' ? -comparison : comparison;
      });

      return quotes;
    } catch (error) {
      return [];
    }
  });

  const quotesByStatus = computed(() => {
    try {
      const statusGroups = {};
      const itemsList = baseStore.itemsList;
      const quotes = Array.isArray(itemsList) ? itemsList : [];
      quotes.forEach((quote) => {
        const status = quote.status;
        if (!statusGroups[status]) {
          statusGroups[status] = [];
        }
        statusGroups[status].push(quote);
      });
      return statusGroups;
    } catch (error) {
      return {};
    }
  });

  const quoteStatistics = computed(() => {
    try {
      const itemsList = baseStore.itemsList;
      const quotes = Array.isArray(itemsList) ? itemsList : [];
      const stats = {
        total: quotes.length,
        draft: 0,
        pending: 0,
        sent: 0,
        accepted: 0,
        rejected: 0,
        expired: 0,
        totalValue: 0,
        acceptedValue: 0,
        averageValue: 0,
      };

      quotes.forEach((quote) => {
        stats[quote.status] = (stats[quote.status] || 0) + 1;
        stats.totalValue += quote.total || 0;
        
        if (quote.status === 'accepted') {
          stats.acceptedValue += quote.total || 0;
        }
      });

      stats.averageValue = stats.total > 0 ? stats.totalValue / stats.total : 0;

      return stats;
    } catch (error) {
      return {
        total: 0,
        draft: 0,
        pending: 0,
        sent: 0,
        accepted: 0,
        rejected: 0,
        expired: 0,
        totalValue: 0,
        acceptedValue: 0,
        averageValue: 0,
      };
    }
  });

  // Quote-specific actions
  const selectQuote = (quoteId) => {
    selectedQuoteId.value = quoteId;
  };

  const clearSelection = () => {
    selectedQuoteId.value = null;
  };

  const updateFilters = (newFilters) => {
    quoteFilters.value = { ...quoteFilters.value, ...newFilters };
  };

  const clearFilters = () => {
    quoteFilters.value = {
      status: null,
      organization_id: null,
      site_id: null,
      date_from: null,
      date_to: null,
      search: '',
    };
  };

  const setSorting = (field, order = 'desc') => {
    quoteSortBy.value = field;
    quoteSortOrder.value = order;
  };

  // Real quote operations (replacing Enhanced mock functions)
  const duplicateQuote = async (quoteId) => {
    const originalQuote = baseStore.getById(quoteId);
    if (!originalQuote) {
      throw new Error('Quote not found');
    }

    const duplicateData = {
      ...originalQuote,
      quote_number: `${originalQuote.quote_number}-COPY`,
      status: 'draft',
      created_at: undefined,
      updated_at: undefined,
      expires_at: null,
      // Remove ID and system fields
      id: undefined,
      items: originalQuote.items.map((item) => ({
        ...item,
        id: undefined,
        quote_id: undefined,
      })),
      labor_items: originalQuote.labor_items.map((item) => ({
        ...item,
        id: undefined,
        quote_id: undefined,
      })),
    };

    return baseStore.create(duplicateData);
  };


  const archiveQuote = async (quoteId) => {
    const quote = baseStore.getById(quoteId);
    if (!quote) {
      throw new Error('Quote not found');
    }

    return baseStore.update(quoteId, {
      ...quote,
      status: 'archived',
      archived_at: new Date().toISOString(),
    });
  };

  const generateQuoteNumber = () => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const timestamp = Date.now().toString().slice(-6);
    return `Q${year}${month}-${timestamp}`;
  };

  // Document upload support
  const uploadDocument = async (quoteId, documentData) => {
    try {
      // If documentData contains a file object, we need to create FormData
      const formData = new FormData();

      if (documentData.file) {
        formData.append('file', documentData.file);
      }
      if (documentData.type) {
        formData.append('type', documentData.type);
      }
      if (documentData.description) {
        formData.append('description', documentData.description);
      }

      // Use quoteService to handle the upload
      const result = await quoteService.uploadDocument(quoteId, formData);

      // Update the quote in the store with the new document
      const quote = baseStore.getById(quoteId);
      if (quote && result.data) {
        await baseStore.update(quoteId, {
          documents: [...(quote.documents || []), result.data],
        });
      }

      return result.data || result;
    } catch (error) {
      throw error;
    }
  };

  // Delete document support
  const deleteDocument = async (quoteId, documentId) => {
    try {
      await quoteService.deleteDocument(quoteId, documentId);

      // Update the quote in the store to remove the document
      const quote = baseStore.getById(quoteId);
      if (quote) {
        await baseStore.update(quoteId, {
          documents: (quote.documents || []).filter((doc) => doc.id !== documentId),
        });
      }

      return true;
    } catch (error) {
      throw error;
    }
  };

  // Phase 3: Tender-Quote Architecture - Versioning Support
  const createQuoteVersion = async (quoteId, options = {}) => {
    return quoteService.createQuoteVersion(quoteId, options);
  };

  const getQuoteVersions = async (quoteId) => {
    try {
      // First try to get the quote from store
      let quote = baseStore.getById(quoteId);

      // If not in store, fetch it from API to get tender_id and option_name
      if (!quote) {
        quote = await quoteService.getQuote(quoteId);
        if (!quote) {
          return [];
        }
      }

      // Check if quote has tender_id and option_name required for versioning
      if (!quote.tender_id || !quote.option_name) {
        if (quote.version) {
          return [quote];
        }
        return [];
      }

      // Fetch all quotes for the same tender and option
      const result = await quoteService.getQuotes({
        tender_id: quote.tender_id,
        option_name: quote.option_name,
      });

      const versions = result.data || result;
      if (Array.isArray(versions) && versions.length > 0) {
        // Sort by version number (descending - highest version first)
        return versions.sort((a, b) => b.version - a.version);
      }

      // If no versions found but we have the current quote with version, return it
      if (quote.version) {
        return [quote];
      }

      return [];
    } catch (error) {
      // Versioning is a non-critical feature
      return [];
    }
  };

  const createQuoteForTender = async (tenderId, quoteData) => {
    return quoteService.createQuoteForTender(tenderId, quoteData);
  };

  return {
    // Base store functionality
    ...baseStore,
    
    // Aliases for backward compatibility
    quotes: computed(() => {
      try {
        const itemsList = baseStore.itemsList;
        return Array.isArray(itemsList) ? itemsList : [];
      } catch (error) {
        return [];
      }
    }),
    fetchQuotes: async (params = {}, force = false) => {
      try {
        const result = await baseStore.fetchAll(params, force);
        return result;
      } catch (error) {
        throw error;
      }
    },
    fetchQuote: async (id, params = {}) => {
      try {

        // Create a cache key based on the request parameters
        const cacheKey = `${id}-${JSON.stringify(params)}`;

        // Check if we already have an in-flight request for this exact fetch
        if (fetchPromiseCache.has(cacheKey)) {
          return fetchPromiseCache.get(cacheKey);
        }

        // Create the fetch promise
        const fetchPromise = (async () => {
          // If params include additional query parameters, make a custom request
          if (params.includeDetails || params.includeParts || params.include) {
            const queryParams = {};
            if (params.includeDetails) {
              queryParams.includeDetails = 'true';
            }
            if (params.includeParts) {
              queryParams.includeParts = 'true';
            }
            if (params.include) {
              queryParams.include = params.include;
            }

            return quoteService.getQuote(id, queryParams);
          } else {
            // Use the base store's fetchById method for simple fetches
            const result = await baseStore.fetchById(id, params.force || false);
            return result;
          }
        })();

        // Store the promise in the cache
        fetchPromiseCache.set(cacheKey, fetchPromise);

        // Clean up the cache after the promise resolves or rejects
        fetchPromise.finally(() => {
          // Remove from cache after a short delay to handle rapid successive calls
          setTimeout(() => {
            fetchPromiseCache.delete(cacheKey);
          }, 100);
        });

        return fetchPromise;
      } catch (error) {
        throw error;
      }
    },
    
    // Additional state
    selectedQuoteId,
    quoteFilters,
    quoteSortBy,
    quoteSortOrder,
    
    // Enhanced computed
    selectedQuote,
    filteredQuotes,
    quotesByStatus,
    quoteStatistics,
    
    // Quote-specific actions
    selectQuote,
    clearSelection,
    updateFilters,
    clearFilters,
    setSorting,
    duplicateQuote,
    archiveQuote,
    generateQuoteNumber,

    // Phase 3: Versioning actions
    createQuoteVersion,
    getQuoteVersions,
    createQuoteForTender,

    // Document management
    uploadDocument,
    deleteDocument,
    removeDocument: deleteDocument,  // Alias for consistency
  };
});